import { formatPersonName } from "../components/personName";
import { formatVerkaufStreckeQuantity } from "../components/streckeMappings";
import { Fragment, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography,
  Snackbar,
  Alert,
  Tooltip
} from "@mui/material";
import RecordLink from "../components/RecordLink";
import { useSelectedRecord, useTargetList } from "../components/useRecordTarget";
import Delete from "@mui/icons-material/Delete";
import GpsFixed from "@mui/icons-material/GpsFixed";
import api from "../api/axios";
import VerkaufDialog from "../components/VerkaufDialog";
import { getVerkaufAltersklasse, getVerkaufWildart } from "../components/verkaufMappings";
import dayjs from "dayjs";
import { useBusinessContext } from "../BusinessContext";
import API_BASE_URL from "../config";
import PageHeader from "../components/PageHeader";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';

function Verkauf() {
  const [verkaufListe, setVerkaufListe] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeVerkauf, setActiveVerkauf] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { withContext } = useBusinessContext();

  const { notice, loading, retry } = useTargetList(`${API_BASE_URL}/verkauf`, "v_id", setVerkaufListe);
  const { selected, targetRef } = useSelectedRecord(verkaufListe, "v_id", setActiveVerkauf);

  const handleSaveVerkauf = (verkaufData) => {
    setDialogOpen(false);
  };

  const handleExpandClick = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const handleDelete = (entry) => {
    if (!window.confirm("Soll dieser Verkauf wirklich gelöscht werden?")) return;

    api.delete(withContext(`${API_BASE_URL}/verkauf/${entry.v_id}`))
      .then(() => {
        setVerkaufListe(current => current.filter(item => item.v_id !== entry.v_id));
        setActiveVerkauf(current => current?.v_id === entry.v_id ? null : current);
        setExpandedRow(null);
        setSnackbar({ open: true, message: "Verkauf erfolgreich gelöscht.", severity: "success" });
      })
      .catch(error => {
        const message = error.response?.data?.error || "Der Verkauf konnte nicht gelöscht werden.";
        setSnackbar({ open: true, message, severity: "error" });
      });
  };

  // Details für die rechte Seite
  const getVerkaufDetails = (verkauf) => {
    if (!verkauf) {
      return (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Bitte wählen Sie einen Verkauf aus der Liste aus.
        </Typography>
      );
    }
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Verkaufsdetails</Typography>
        <Typography><b>Id:</b> {verkauf.v_id}</Typography>
        <Typography><b>Datum:</b> {verkauf.v_datum ? dayjs(verkauf.v_datum).format("DD.MM.YYYY") : ""}</Typography>
        <Typography><b>Kunde:</b> {formatPersonName(verkauf.k_nachname, verkauf.k_vorname)}</Typography>
        <Typography><b>Menge:</b> {verkauf.v_kg || verkauf.v_menge} {verkauf.v_einheit}</Typography>
        <Typography><b>Betrag:</b> {verkauf.v_betrag} €</Typography>
        <Typography><b>Bezahlt:</b> {verkauf.v_bezahlt ? "Ja" : "Nein"}</Typography>
        <Typography><b>Bemerkung:</b> {verkauf.v_bemerkung}</Typography>
        <Typography sx={{ mt: 2 }} variant="h6">Streckenposition</Typography>
        <Typography><b>Wild:</b> {getVerkaufWildart(verkauf)}</Typography>
        <Typography><b>Altersklasse:</b> {getVerkaufAltersklasse(verkauf)}</Typography>
        <Typography><b>Jäger:</b> {formatPersonName(verkauf.j_name, verkauf.j_vorname)}</Typography>
        <Typography><b>Datum:</b> {verkauf.s_datum ? dayjs(verkauf.s_datum).format("DD.MM.YYYY") : ""}</Typography>
        <Typography><b>Ort:</b> {verkauf.s_ort}</Typography>
        <Typography><b>Menge:</b> {formatVerkaufStreckeQuantity(verkauf)}</Typography>
        <Typography><b>Bemerkung:</b> {verkauf.s_bemerkung}</Typography>
      </Box>
    );
  };

  // Streckeninfo für Collapse
  const getStreckenInfo = (streckeId, verkauf) => {
    // Alle Infos direkt aus verkauf-Objekt verwenden
    return (
      <Box sx={{ margin: 1, ml: 4, bgcolor: "#f5f5f5", borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Streckenposition
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Datum</TableCell>
              <TableCell>Wildname</TableCell>
              <TableCell>Altersklasse</TableCell>
              <TableCell>Jäger</TableCell>
              <TableCell align="right">Menge</TableCell>
              <TableCell>Ort</TableCell>
              <TableCell>Bemerkung</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <RecordLink page="strecke" id={verkauf.s_id}>{verkauf.s_datum ? dayjs(verkauf.s_datum).format("DD.MM.YYYY") : ""}</RecordLink>
              </TableCell>
              <TableCell><RecordLink page="strecke" id={verkauf.s_id}>{getVerkaufWildart(verkauf)}</RecordLink></TableCell>
              <TableCell><RecordLink page="strecke" id={verkauf.s_id}>{getVerkaufAltersklasse(verkauf)}</RecordLink></TableCell>
              <TableCell>{formatPersonName(verkauf.j_name, verkauf.j_vorname)}</TableCell>
              <TableCell align="right">{formatVerkaufStreckeQuantity(verkauf)}</TableCell>
              <TableCell>{verkauf.s_ort}</TableCell>
              <TableCell>{verkauf.s_bemerkung}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box sx={{ flex: 1 }}>
        <PageHeader title="Wildverkauf" />
        {loading && <LoadingIndicator label="Verkäufe werden geladen …" />}
        {notice && (notice.severity === 'error' ? <LoadError onRetry={retry}>{notice.text}</LoadError> : <Alert severity={notice.severity}>{notice.text}</Alert>)}

        <VerkaufDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleSaveVerkauf}
          streckenListe={[]} // Keine Streckenliste nötig, da alles im Verkaufsobjekt
        />
        <Paper sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Datum</TableCell>
                <TableCell>Wild</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell>Menge</TableCell>
                <TableCell>Betrag (€)</TableCell>
                <TableCell>Bezahlt</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verkaufListe.map((entry, index) => (
                <Fragment key={entry.v_id}>
                  <TableRow
                    hover
                    ref={String(entry.v_id) === selected ? targetRef : undefined}
                    selected={activeVerkauf && activeVerkauf.v_id === entry.v_id}
                    onClick={() => setActiveVerkauf(entry)}
                  >
                    <TableCell sx={{ width: 40 }}>
                      <Tooltip title={expandedRow === index ? "Streckendetails ausblenden" : "Streckendetails anzeigen"}>
                        <IconButton
                          size="small"
                          color={expandedRow === index ? "primary" : "default"}
                          aria-label={expandedRow === index ? "Streckendetails ausblenden" : "Streckendetails anzeigen"}
                          aria-expanded={expandedRow === index}
                          onClick={e => {
                            e.stopPropagation();
                            handleExpandClick(index);
                          }}
                        >
                          <GpsFixed fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {entry.v_datum ? dayjs(entry.v_datum).format("DD.MM.YYYY") : ""}
                    </TableCell>
                    <TableCell>{getVerkaufWildart(entry)}</TableCell>
                    <TableCell>{formatPersonName(entry.k_nachname, entry.k_vorname)}</TableCell>
                    <TableCell>{entry.v_kg || entry.v_menge} {entry.v_einheit}</TableCell>
                    <TableCell>{entry.v_betrag} €</TableCell>
                    <TableCell>{entry.v_bezahlt ? "Ja" : "Nein"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <IconButton
                        aria-label={`Verkauf ${entry.v_id} löschen`}
                        color="error"
                        size="small"
                        onClick={event => {
                          event.stopPropagation();
                          handleDelete(entry);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                      <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                        {getStreckenInfo(entry.s_id, entry)}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
      <Box sx={{ minWidth: 300, maxWidth: 400, bgcolor: "#f5f5f5", p: 2, borderRadius: 2, height: "fit-content" }}>
        {getVerkaufDetails(activeVerkauf)}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(current => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Verkauf;
